/**
 * Authentication types — mirror the NestJS backend `AuthenticatedUser`.
 *
 * Roles match the values seeded in the backend (Phase 2a). The frontend
 * uses them for route guards and conditional UI; canonical role checks
 * always remain server-side via RolesGuard.
 */
export const ROLES = [
  'isi_admin',
  'superadmin',
  'fleet_manager',
  'chief',
  'technician',
] as const;

export type Role = (typeof ROLES)[number];

export interface AuthenticatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  preferredLang: 'fr' | 'en' | 'es';
  roles: Role[];
  companyId?: string | null;
  vesselIds?: string[];
  /** L12 — ISO timestamp when the user finished TOTP enrollment. */
  totpEnabledAt?: string | null;
  /** L12 — true when policy forces 2FA for this user (auto-set
   *  for superadmin / isi_admin). */
  totpRequiredByPolicy?: boolean;
}

/** Convenience: full display name fallbacks. */
export function fullName(u: AuthenticatedUser): string {
  const composed = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return composed.length > 0 ? composed : u.email;
}

/** True when the user holds at least one of the given roles. */
export function hasAnyRole(u: AuthenticatedUser | null | undefined, roles: Role[]): boolean {
  if (!u) return false;
  return roles.some((r) => u.roles.includes(r));
}
